import { useEffect, useState } from 'react';
import copy from 'copy-to-clipboard';
import SidepanelDefaultRow from '../../side-panel/side-panel-default-row';
import TransactionAccountRow from '../account-row';
import formatDate from '../../../helpers/format-date';
import { Currency } from '../types';
import ButtonSecondary from '../../button/button-secondary';
import LinkSecondaryButton from '../../link-pseudo-button/link-secondary-button';
import IconLoader from '../../icons/loader';
import formatBalance from '../../../helpers/format-balance';
import IconCopy from '../../icons/copy-two';
import { GeneralLedgerContentReadTypes } from './types';
import Tabs from '../../tabs/tabs';
import ChangeLogs from '../../change-logs/change-logs';
import { PermissionEnum } from '../../../base-types';
import TransactionDocuments from '../documents/documents';

import styles from '../transaction.module.css';

export default function GeneralLedgerContentRead({
  transaction,
  children,
  onDelete,
  linkedTransactionPath,
  debitAccountBalance,
  creditAccountBalance,
  debitBalanceIsLoading,
  creditBalanceIsLoading,
  backPath,
  currenciesList,
  baseCurrency,
  changeLogsIsLoading,
  changeLogs,
  activePermission,
  users,
  transactionChangeLogsPagination,
  changeLogsIsFetching,
  getTransactionChangeLogs,
  getTransationDocumentsAction,
  documentsIsLoading,
  documentsIsFetching,
  documents,
  documentsPagination,
  setSubPanelVisible,
  setSelectedDocumentName,
  setTransactionSidepanelVisible,
  onUploadDocument,
  uploadDocumentInProgress,
  uploadDocumentIsSuccess,
  uploadDocumentErrors,
  selectedDocumentId,
  setSelectedDocumentId,
  downloadInProgress,
  onDownloadDocument,
  getTransactionDocument,
  attachedDocumentsErrors,
  uploadedDocumentsErrors,
  activeTab,
  setActiveTab,
  deleteDisabled,
}: GeneralLedgerContentReadTypes): React.JSX.Element {
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);

  const onCopy = (copyValue: string) => {
    setCopyIsSuccess(true);
    copy(copyValue);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  const currentCurrency = currenciesList?.find(
    (item) => item.id === transaction?.currencyId,
  );

  useEffect(() => {
    if (
      activeTab === 'CHANGE_LOGS' &&
      getTransactionChangeLogs &&
      activePermission?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getTransactionChangeLogs();
    }

    if (
      activeTab === 'DOCUMENTS' &&
      getTransationDocumentsAction &&
      activePermission?.includes(
        PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DOCUMENTS_LIST,
      )
    ) {
      getTransationDocumentsAction();
    }
  }, [activeTab]);

  return (
    <>
      {transaction?.deletedAt && (
        <div className={styles.deleted}>{`Transaction deleted ${formatDate(
          transaction?.deletedAt,
          true,
        )}`}</div>
      )}
      <Tabs
        tabs={[
          {
            name: 'General Info',
            value: 'GENERAL',
          },
          {
            name: 'Change Logs',
            value: 'CHANGE_LOGS',
          },
          {
            name: 'Documents',
            value: 'DOCUMENTS',
          },
        ]}
        activeTab={activeTab}
        onClick={(value) => setActiveTab(value)}
        wrapperClassName={styles.glTabs}
        withSticky
      />
      {activeTab === 'GENERAL' && (
        <>
          {children}
          <div className={styles.transactionInfoWrapper}>
            <div className={styles.transactionBlockWrapper}>
              <SidepanelDefaultRow
                title="Date"
                valueClassName={styles.readCopyButtonWrapper}
              >
                {formatDate(transaction?.transferDate || '', true)}
                <button
                  type="button"
                  className={`${styles.readCopyButton} ${
                    copyIsSuccess ? styles.success : ''
                  }`}
                  onClick={() =>
                    onCopy(formatDate(transaction?.transferDate || '', true))
                  }
                  aria-label="Copy"
                >
                  <IconCopy />
                </button>
              </SidepanelDefaultRow>
              {transaction?.sum && (
                <SidepanelDefaultRow
                  title="Amount"
                  valueClassName={styles.readCopyButtonWrapper}
                >
                  {`${formatBalance(
                    transaction?.sum,
                    currentCurrency,
                  )} ${transaction?.currency?.name || ''}`}
                  <button
                    type="button"
                    className={`${styles.readCopyButton} ${
                      copyIsSuccess ? styles.success : ''
                    }`}
                    onClick={() => onCopy(transaction?.sum?.toString())}
                    aria-label="Copy"
                  >
                    <IconCopy />
                  </button>
                </SidepanelDefaultRow>
              )}
              <SidepanelDefaultRow
                title="Amount at Base currency"
                valueClassName={styles.readCopyButtonWrapper}
              >
                {transaction?.sumBaseCurrency
                  ? `${formatBalance(transaction?.sumBaseCurrency, {
                      decimals: 99,
                    } as Currency)} ${baseCurrency?.name || ''}`
                  : '-'}
                <button
                  type="button"
                  className={`${styles.readCopyButton} ${
                    copyIsSuccess ? styles.success : ''
                  }`}
                  onClick={() => {
                    if (transaction?.sumBaseCurrency) {
                      onCopy(transaction?.sumBaseCurrency?.toString());
                    }
                  }}
                  aria-label="Copy"
                >
                  <IconCopy />
                </button>
              </SidepanelDefaultRow>
            </div>

            <div className={styles.transactionBlockWrapper}>
              <TransactionAccountRow
                title="Debit account"
                name={transaction?.debitAccount?.description}
                number={transaction?.debitAccount?.fullNumber}
                onCopy={onCopy}
                copyIsSuccess={copyIsSuccess}
              />
              <TransactionAccountRow
                title="Credit account"
                name={transaction?.creditAccount?.description}
                number={transaction?.creditAccount?.fullNumber}
                onCopy={onCopy}
                copyIsSuccess={copyIsSuccess}
              />
            </div>

            <div className={styles.transactionBlockWrapper}>
              <SidepanelDefaultRow
                title="Current balance Debit"
                valueClassName={styles.readCopyButtonWrapper}
              >
                {!debitBalanceIsLoading && (
                  <div>
                    {debitAccountBalance
                      ? `${formatBalance(
                          debitAccountBalance.value,
                          currentCurrency,
                        )} ${transaction?.currency?.name || ''}`
                      : `${formatBalance(0, currentCurrency)} ${
                          transaction?.currency?.name || ''
                        }`}
                  </div>
                )}

                {debitBalanceIsLoading && <IconLoader width={14} height={14} />}
                <button
                  type="button"
                  className={`${styles.readCopyButton} ${
                    copyIsSuccess ? styles.success : ''
                  }`}
                  onClick={() =>
                    onCopy(
                      debitAccountBalance
                        ? debitAccountBalance.value?.toString()
                        : '0',
                    )
                  }
                  aria-label="Copy"
                >
                  <IconCopy />
                </button>
              </SidepanelDefaultRow>
              <SidepanelDefaultRow
                title="Current balance Credit"
                valueClassName={styles.readCopyButtonWrapper}
              >
                {!creditBalanceIsLoading && (
                  <div>
                    {creditAccountBalance
                      ? `${formatBalance(
                          creditAccountBalance.value,
                          currentCurrency,
                        )} ${transaction?.currency?.name || ''}`
                      : `${formatBalance(0, currentCurrency)} ${
                          transaction?.currency?.name || ''
                        }`}
                  </div>
                )}
                {creditBalanceIsLoading && (
                  <IconLoader width={14} height={14} />
                )}
                <button
                  type="button"
                  className={`${styles.readCopyButton} ${
                    copyIsSuccess ? styles.success : ''
                  }`}
                  onClick={() =>
                    onCopy(
                      creditAccountBalance
                        ? creditAccountBalance.value?.toString()
                        : '0',
                    )
                  }
                  aria-label="Copy"
                >
                  <IconCopy />
                </button>
              </SidepanelDefaultRow>
            </div>
            <div className={styles.transactionBlockWrapper}>
              <SidepanelDefaultRow
                title="Document number"
                valueClassName={styles.readCopyButtonWrapper}
              >
                {transaction?.documentNumber}
                <button
                  type="button"
                  className={`${styles.readCopyButton} ${
                    copyIsSuccess ? styles.success : ''
                  }`}
                  onClick={() => onCopy(transaction?.documentNumber || '')}
                  aria-label="Copy"
                >
                  <IconCopy />
                </button>
              </SidepanelDefaultRow>
              <SidepanelDefaultRow
                title="Info"
                valueClassName={styles.readCopyButtonWrapper}
              >
                {transaction?.info}
                <button
                  type="button"
                  className={`${styles.readCopyButton} ${
                    copyIsSuccess ? styles.success : ''
                  }`}
                  onClick={() => onCopy(transaction?.info || '')}
                  aria-label="Copy"
                >
                  <IconCopy />
                </button>
              </SidepanelDefaultRow>
              <SidepanelDefaultRow
                title="GL Transaction  ID"
                valueClassName={styles.readCopyButtonWrapper}
              >
                {transaction?.id}
                <button
                  type="button"
                  className={`${styles.readCopyButton} ${
                    copyIsSuccess ? styles.success : ''
                  }`}
                  onClick={() => onCopy(transaction?.id || '')}
                  aria-label="Copy"
                >
                  <IconCopy />
                </button>
              </SidepanelDefaultRow>
              <SidepanelDefaultRow
                title="ID of the linked transaction"
                valueClassName={styles.readCopyButtonWrapper}
              >
                {transaction?.parentTransactionId}
                <button
                  type="button"
                  className={`${styles.readCopyButton} ${
                    copyIsSuccess ? styles.success : ''
                  }`}
                  onClick={() => onCopy(transaction?.parentTransactionId || '')}
                  aria-label="Copy"
                >
                  <IconCopy />
                </button>
              </SidepanelDefaultRow>

              {linkedTransactionPath && (
                <LinkSecondaryButton
                  text="Open linked transaction"
                  path={linkedTransactionPath}
                  backPath={backPath}
                  target="_blank"
                />
              )}
            </div>

            {onDelete && !deleteDisabled && (
              <div className={styles.transactionBlockWrapper}>
                <ButtonSecondary
                  text="Delete transaction"
                  className="button-white button-warning"
                  onClick={onDelete}
                />
              </div>
            )}
          </div>
        </>
      )}
      {activeTab === 'CHANGE_LOGS' && (
        <ChangeLogs
          entityType="GENERAL_LEDGER_TRANSACTION"
          entityId={transaction?.id as string}
          changeLogsIsLoading={changeLogsIsLoading}
          changeLogs={changeLogs}
          activePermission={activePermission}
          users={users}
          pagination={transactionChangeLogsPagination}
          changeLogsIsFetching={changeLogsIsFetching}
          getChangeLogs={getTransactionChangeLogs}
        />
      )}
      {activeTab === 'DOCUMENTS' && (
        <TransactionDocuments
          documents={documents}
          setSubPanelVisible={setSubPanelVisible}
          setSelectedDocumentName={setSelectedDocumentName}
          setTransactionSidepanelVisible={setTransactionSidepanelVisible}
          onUploadDocument={onUploadDocument}
          uploadDocumentInProgress={uploadDocumentInProgress}
          uploadDocumentIsSuccess={uploadDocumentIsSuccess}
          uploadDocumentErrors={uploadDocumentErrors}
          selectedDocumentId={selectedDocumentId}
          setSelectedDocumentId={setSelectedDocumentId}
          downloadInProgress={downloadInProgress}
          activePermission={activePermission}
          onDownloadDocument={onDownloadDocument}
          getTransactionDocument={getTransactionDocument}
          documentsIsLoading={documentsIsLoading}
          documentsPagination={documentsPagination}
          getTransactionDocuments={getTransationDocumentsAction}
          documentsIsFetching={documentsIsFetching}
          attachedDocumentsErrors={attachedDocumentsErrors}
          uploadedDocumentsErrors={uploadedDocumentsErrors}
          glDocuments
        />
      )}
    </>
  );
}
